import { useEffect } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse
} from "react-router";

import { t } from "i18next";

import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import { theme } from "@/src/theme/theme";

import { WholePageSpinner } from "@/src/components/atoms";

import { initLocalization } from "@/src/localizations";
import RouteErrorView from "@/src/pages/handlers/RouteErrorView";
import { AppProviders } from "@/src/providers/AppProviders";
import { initDigitalDataSync } from "@/src/utils/digitalData/bootstrap";

import type { Route } from "./+types/root";

import * as Sentry from "@sentry/react";

void initLocalization();
initDigitalDataSync();

const DEFAULT_TITLE =
  "Baana - Kilpailuta auton myynti liikkeeseen - löydä paras hinta";
const DEFAULT_DESCRIPTION =
  "Myy autosi, moottoripyöräsi tai karavaanisi liikkeelle nopeasti, parhaalla hinnalla. Kilpailuta kerralla liikkeet ympäri Suomen ja tee kaupat jopa samana päivänä. Kilpailuta liikkeiden tarjoukset!";
const DEFAULT_OG_IMAGE =
  "https://baana.nettiauto.com/assets/images/baana-by-nettiauto-logo-red.png";

// Root-level defaults so every route has a tab title and social tags, matching
// the static defaults the pre-framework-mode index.html used to provide. Routes
// that export their own meta() fully override these.
export const meta: Route.MetaFunction = () => [
  { title: DEFAULT_TITLE },
  { name: "description", content: DEFAULT_DESCRIPTION },
  { property: "og:title", content: DEFAULT_TITLE },
  { property: "og:site_name", content: "Baana" },
  { property: "og:description", content: DEFAULT_DESCRIPTION },
  { property: "og:image", content: DEFAULT_OG_IMAGE },
  { property: "og:type", content: "website" }
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="fi">
      <head>
        <meta charSet="UTF-8" />
        <meta name="google" content="notranslate" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=8"
        />
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link
          rel="icon"
          href="/assets/images/baana_favicon_512x512.svg"
          type="image/svg+xml"
        />
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              if (document.location.host === "prod.baana.com") {
                window.location = "https://baana.nettiauto.com" + window.location.pathname;
              }
              if (typeof global === "undefined") { var global = window; }
            `
          }}
        />
        <ABTastyScript />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  return (
    <AppProviders>
      <ThemeProvider theme={theme} defaultMode="light">
        <CssBaseline />
        <Outlet />
      </ThemeProvider>
    </AppProviders>
  );
}

// Shown during the initial client render window while JS modules load and any
// `clientLoader`s run. With `ssr: false` the prerendered shell would otherwise
// sit blank until hydration; this renders the app-wide loading state instead,
// matching every other whole-page loading state in the product. Kept to the MUI
// theme only — mounting `AppProviders` here would fire auth/consent/analytics
// side effects that immediately tear down when `Root` mounts after hydration.
export function HydrateFallback() {
  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <WholePageSpinner />
    </ThemeProvider>
  );
}

const isNotFound = (error: unknown): boolean =>
  isRouteErrorResponse(error) && error.status === 404;

const isBlogPath = (): boolean =>
  typeof window !== "undefined" && window.location.pathname.startsWith("/blog");

export function ErrorBoundary({ error }: { error?: unknown }) {
  console.error("[root.tsx ErrorBoundary]", error);
  useEffect(() => {
    // 404s are expected routing outcomes, not faults worth alerting on.
    if (error && !isNotFound(error)) {
      Sentry.captureException(error);
    }
  }, [error]);

  const notFound = isNotFound(error);

  return (
    <AppProviders>
      <ThemeProvider theme={theme} defaultMode="light">
        <CssBaseline />
        <RouteErrorView
          title={
            notFound ? t("general.notFoundTitle") : t("general.errorTitle")
          }
          message={
            notFound
              ? t("general.pageNotFound")
              : t("general.somethingWentWrong")
          }
          showRetry={!notFound}
          showBackToBlog={isBlogPath()}
        />
        {import.meta.env.DEV && (
          <pre
            style={{
              textAlign: "left",
              overflow: "auto",
              fontSize: "0.75rem",
              padding: "1rem"
            }}
          >
            {error instanceof Error ? error.stack : String(error)}
          </pre>
        )}
      </ThemeProvider>
    </AppProviders>
  );
}

function ABTastyScript() {
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `
          (function () {
            var src = "${import.meta.env.VITE_ABTASTY_SCRIPT_URL || ""}";
            if (!src) return;
            var REQUIRED = ["functional", "personalization", "analytics"];
            var REPETITIVE = "alma_cmp_v3_consent_available_repetitive";
            var CHANGED = "alma_cmp_v3_consent_changed";
            var loaded = false;
            function hasAll(purposes) {
              return REQUIRED.every(function (p) { return purposes.includes(p); });
            }
            function loadTag() {
              if (loaded) return;
              loaded = true;
              var s = document.createElement("script");
              s.src = src;
              document.head.appendChild(s);
            }
            function onConsent(e) {
              var d = e.detail;
              if (!d || !d.purposes) return;
              if (hasAll(d.purposes)) {
                loadTag();
                globalThis.abtastyGrantConsent = true;
                globalThis.dispatchEvent(new CustomEvent("abtasty_grantConsent", {}));
              } else {
                globalThis.dispatchEvent(new CustomEvent("abtasty_revokeConsent", {}));
              }
            }
            document.addEventListener(REPETITIVE, onConsent);
            document.addEventListener(CHANGED, onConsent);
          })();
        `
      }}
    />
  );
}
